import { Injectable } from '@angular/core';
import { AdminNavItem } from '../../../models/admin-nav-item.model';

interface AdminMenuStoragePayload {
  version: number;
  items: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AdminMenuStorageService {
  private static readonly STORAGE_PREFIX = 'admin-menu-order';
  private static readonly STORAGE_VERSION = 1;

  applyPersistedOrder(items: AdminNavItem[]): AdminNavItem[] {
    const normalizedBase = this.normalizeItems(items);
    const savedRoutes = this.readSavedRoutes();

    if (!savedRoutes.length) {
      return normalizedBase;
    }

    const itemByRoute = new Map(
      normalizedBase.map((item) => [item.route, item] as const)
    );

    const persistedItems = savedRoutes
      .map((route) => itemByRoute.get(route))
      .filter((item): item is AdminNavItem => !!item);

    const persistedRoutes = new Set(persistedItems.map((item) => item.route));

    const remainingItems = normalizedBase.filter(
      (item) => !persistedRoutes.has(item.route)
    );

    return this.normalizeItems([...persistedItems, ...remainingItems]);
  }

  saveOrder(items: AdminNavItem[]): void {
    if (typeof window === 'undefined') {
      return;
    }

    const payload: AdminMenuStoragePayload = {
      version: AdminMenuStorageService.STORAGE_VERSION,
      items: this.normalizeItems(items).map((item) => item.route),
    };

    localStorage.setItem(this.getStorageKey(), JSON.stringify(payload));
  }

  clearSavedOrder(): void {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.removeItem(this.getStorageKey());
  }

  private readSavedRoutes(): string[] {
    if (typeof window === 'undefined') {
      return [];
    }

    const rawValue = localStorage.getItem(this.getStorageKey());

    if (!rawValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(rawValue) as Partial<AdminMenuStoragePayload>;

      if (
        parsed.version !== AdminMenuStorageService.STORAGE_VERSION ||
        !Array.isArray(parsed.items)
      ) {
        return [];
      }

      return parsed.items.filter(
        (item): item is string => typeof item === 'string' && !!item.trim()
      );
    } catch {
      return [];
    }
  }

  private normalizeItems(items: AdminNavItem[]): AdminNavItem[] {
    return [...items]
      .sort(
        (left, right) =>
          (left.order ?? left.originalOrder ?? 999) -
          (right.order ?? right.originalOrder ?? 999)
      )
      .map((item, index) => ({
        ...item,
        originalOrder: item.originalOrder ?? item.order ?? index + 1,
        order: index + 1,
      }));
  }

  private getStorageKey(): string {
    const scope =
      typeof window === 'undefined'
        ? 'server'
        : window.location.hostname.trim() || 'default';

    return `${AdminMenuStorageService.STORAGE_PREFIX}:${scope}`;
  }
}